import dayjs from 'dayjs';

/** Minutes after scheduled start before no-show actions (mark, invoice); matches backend. */
export const NO_SHOW_GRACE_MINUTES_AFTER_START = 15;
/** Minutes after no-show marking before revert needs a special right; matches backend. */
export const NO_SHOW_REVERT_GRACE_MINUTES = 5;

export const isInactiveBookingStatus = status =>
  ['canceled', 'rejected', 'blocked', 'noShow'].includes(status);

export const isTodayOrFuture = input => {
  if (!input) return false;
  const date = dayjs(input);
  if (!date.isValid()) return false;
  return date.isSame(dayjs(), 'day') || date.isAfter(dayjs(), 'day');
};

/**
 * Matches backend buildStaleWaitingForPaymentFilter timing:
 * a payment-related booking state is "actionable" when:
 * - creation >= 2h ago OR
 * - (startTime in < 24h AND creation > 15min ago)
 */
export const isPaymentActionableByTiming = (booking, nowTs = Date.now()) => {
  if (!booking) return false;
  const createdAtRaw =
    booking?.meta?.createdAt || booking?.eventCreatedAt || booking?.createdAt;
  const createdAtTs = createdAtRaw ? new Date(createdAtRaw).getTime() : NaN;
  const startTimeTs = booking?.startTime ? new Date(booking.startTime).getTime() : NaN;
  if (!Number.isFinite(createdAtTs)) return false;

  const twoHoursAgo = nowTs - 2 * 60 * 60 * 1000;
  const fifteenMinutesAgo = nowTs - 15 * 60 * 1000;
  const twentyFourHoursFromNow = nowTs + 24 * 60 * 60 * 1000;

  if (createdAtTs <= twoHoursAgo) return true;
  if (
    Number.isFinite(startTimeTs) &&
    startTimeTs <= twentyFourHoursFromNow &&
    createdAtTs <= fifteenMinutesAgo
  ) {
    return true;
  }
  return false;
};


/**
 * Matches backend markNoShow timing: operator may mark no-show when now is at or after
 * scheduled start plus NO_SHOW_GRACE_MINUTES_AFTER_START.
 */
export const isNoShowActionable = (booking, nowValue = Date.now()) => {
  if (!booking) return false;
  if (booking.status !== 'confirmed') return false;
  if (booking.presentSince) return false;
  if (Number(booking.participantCount || 0) <= 0) return false;

  const start = booking?.startTime ? dayjs(booking.startTime) : null;
  if (!start?.isValid()) return false;

  const now = dayjs(nowValue);
  const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
  return now.isAfter(eligibleAt) || now.isSame(eligibleAt);
};

/**
 * Matches backend sendNoShowInvoice timing: invoice send is allowed when
 * now is at or after scheduled start plus NO_SHOW_GRACE_MINUTES_AFTER_START.
 */
export const isNoShowInvoiceSendable = (booking, nowValue = Date.now()) => {
  const start = booking?.startTime ? dayjs(booking.startTime) : null;
  if (!start?.isValid()) return false;
  const now = dayjs(nowValue);
  const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
  return now.isAfter(eligibleAt) || now.isSame(eligibleAt);
};

export const hasNoShowInvoiceBillableLineItems = booking => {
  if (!booking || booking.fullyPaid === true) return false;
  const lineItems = Array.isArray(booking.lineItems) ? booking.lineItems : [];
  return lineItems.some(item => {
    if (!item || item.status === 'canceled' || !(item.count > 0)) return false;
    const refs = Array.isArray(item.paymentReferences) ? item.paymentReferences : [];
    return refs.some(
      ref => ref && ref.paymentType === 'offline' && ref.count > 0 && !ref.isPaid
    );
  });
};

export const isNoShowRevertInGracePeriod = (booking, nowValue = Date.now()) => {
  if (!booking || booking.status !== 'noShow') return false;
  if (booking.noShowInvoiceId || booking.noShowInvoiceSkippedAt || booking.noShowInvoicePaidAt) {
    return false;
  }

  const noShowAt = booking?.noShowAt ? dayjs(booking.noShowAt) : null;
  if (!noShowAt?.isValid()) return false;

  const now = dayjs(nowValue);
  const privilegedAt = noShowAt.add(NO_SHOW_REVERT_GRACE_MINUTES, 'minute');
  return now.isBefore(privilegedAt) || now.isSame(privilegedAt);
};

/**
 * Matches backend buildStaleWaitingForPaymentFilter: a waitingForPayment booking
 * is "actionable" (shown in PendingBookings, highlighted in views) when:
 * - creation >= 2h ago OR
 * - (startTime in < 24h AND creation > 15min ago)
 */
export const isWaitingForPaymentActionable = (booking, nowTs = Date.now()) => {
  if (!booking || booking.status !== 'waitingForPayment') return false;
  return isPaymentActionableByTiming(booking, nowTs);
};
